/**
 * AutoSync — Transparent multi-device sync for localStorage data.
 *
 * Wraps window.localStorage so every write to a known maidly_* key is
 * debounced and pushed to the cloud. On login, all cloud data is pulled
 * down and merged into localStorage. Deletions are detected by diffing
 * the array contents and pushed individually.
 *
 * Field-level conflict resolution:
 *   - Each item carries a `_fields_updated_at` map { fieldName: ISO_timestamp }
 *   - We diff each push against the last snapshot to know which fields actually
 *     changed and stamp only those.
 *   - The backend's bulk-save merges field-by-field, keeping whichever side has
 *     the newer per-field timestamp. Concurrent edits to *different* fields on
 *     the same record both survive.
 *
 * No page-level changes required — pages keep using localStorage as before.
 */

import syncService from './SyncService';
import maidlyDB from './MaidlyDB';
import syncEngine from './SyncEngine';
import { mergeRecordArrays } from '../utils/mergeRecords';

// localStorage key  -->  cloud data_type
const SYNCED_KEYS = {
  maidly_customers: 'customers',
  maidly_invoices: 'invoices',
  maidly_quotes: 'quotes',
  maidly_appointments: 'appointments',
  maidly_timeEntries: 'timeEntries',
  maidly_employees: 'employees',
  maidly_mileageEntries: 'mileageEntries',
  maidly_expenses: 'expenses',
  maidly_settings: 'settings',
};

const REVERSE = Object.fromEntries(
  Object.entries(SYNCED_KEYS).map(([k, v]) => [v, k])
);

const PUSH_DEBOUNCE_MS = 1500;

// Internal field names start with "_" and are never themselves stamped.
const isInternalField = (k) => typeof k === 'string' && k.startsWith('_');

// Stable deep-equal via JSON serialization. Good enough for plain JSON-y values
// stored in localStorage; we never sync class instances or functions.
const deepEqual = (a, b) => {
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return a === b;
  }
};

class AutoSync {
  constructor() {
    this.installed = false;
    this.pending = new Map(); // dataType -> timeoutId
    this.suppressSync = false; // re-entrancy guard during pull
    this.origSetItem = null;
    this.origRemoveItem = null;
    // dataType -> Map<id, full item snapshot> — used both for delete detection
    // AND field-diff so we only stamp truly-changed fields.
    this.lastSnapshot = new Map();
  }

  install() {
    if (this.installed) return;
    this.installed = true;

    this.origSetItem = window.localStorage.setItem.bind(window.localStorage);
    this.origRemoveItem = window.localStorage.removeItem.bind(window.localStorage);

    // Seed snapshots from current localStorage so we can detect deletes/changes.
    Object.keys(SYNCED_KEYS).forEach((key) => {
      this.lastSnapshot.set(SYNCED_KEYS[key], this.itemMapFromKey(key));
    });

    // Wrap setItem
    window.localStorage.setItem = (key, value) => {
      this.origSetItem(key, value);
      if (this.suppressSync) return;
      if (key === 'maidly_settings') {
        // Company info / app settings is a single OBJECT (not a list). Sync it
        // as one cloud record with last-write-wins so it travels across devices.
        if (syncService.getSyncKey()) this.schedulePushSettings(value);
        return;
      }
      const dataType = SYNCED_KEYS[key];
      if (dataType && syncService.getSyncKey()) {
        this.schedulePush(dataType, value);
      }
    };

    // Wrap removeItem
    window.localStorage.removeItem = (key) => {
      this.origRemoveItem(key);
      if (this.suppressSync) return;
      const dataType = SYNCED_KEYS[key];
      if (dataType && syncService.getSyncKey()) {
        // Treat as: array became empty -> delete every previous item
        const prev = this.lastSnapshot.get(dataType) || new Map();
        [...prev.keys()].forEach((id) =>
          syncService.delete(dataType, id).catch(() => {})
        );
        this.lastSnapshot.set(dataType, new Map());
      }
    };
  }

  /**
   * Write already-synced data to a localStorage key WITHOUT re-triggering a
   * cloud push, and refresh the diff snapshot so later genuine edits still
   * diff correctly. Used by SyncService.saveLocal to break the
   * push -> saveLocal -> wrapped-setItem -> push feedback loop.
   */
  writeCache(dataType, data) {
    const key = REVERSE[dataType] || `maidly_${dataType}`;
    const write = this.origSetItem || window.localStorage.setItem.bind(window.localStorage);
    const prev = this.suppressSync;
    this.suppressSync = true;
    try {
      write(key, JSON.stringify(data));
    } finally {
      this.suppressSync = prev;
    }
    // Keep the snapshot aligned with what we just wrote so a subsequent user
    // edit diffs against the real current state (and deletes aren't mis-fired).
    if (Array.isArray(data)) {
      const m = new Map();
      data.forEach((it) => { if (it && it.id) m.set(it.id, it); });
      this.lastSnapshot.set(dataType, m);
    }
  }

  /** Pull all cloud data into localStorage. Call this right after login. */
  async pullFromCloud() {
    if (!this.installed) this.install();
    if (!syncService.getSyncKey()) return { success: false, reason: 'no_user' };
    if (!navigator.onLine) return { success: false, reason: 'offline' };

    try {
      const cloudData = await syncService.bulkLoad();
      if (!cloudData || typeof cloudData !== 'object') {
        return { success: false, reason: 'no_data' };
      }

      this.suppressSync = true;
      try {
        Object.entries(cloudData).forEach(([dataType, items]) => {
          const key = REVERSE[dataType];
          if (!key || !Array.isArray(items)) return;
          // MERGE (last-write-wins) rather than overwrite, so fresh local edits
          // that haven't finished syncing are NOT wiped on reload. Cloud wins
          // only when its copy is newer; records unique to either side survive.
          let localArr = [];
          try { localArr = JSON.parse(localStorage.getItem(key)) || []; } catch { localArr = []; }
          const { merged } = mergeRecordArrays(localArr, items);
          this.origSetItem(key, JSON.stringify(merged));
          const m = new Map();
          merged.forEach((it) => {
            if (it && it.id) m.set(it.id, it);
          });
          this.lastSnapshot.set(dataType, m);
          // Mirror to IndexedDB (best-effort, async)
          maidlyDB.records.put(dataType, merged).catch(() => {});
        });
      } finally {
        this.suppressSync = false;
      }

      // Company info / app settings (single object record). Last-write-wins:
      // only replace local if the cloud copy is strictly newer, so a local edit
      // made just before this pull isn't clobbered.
      let settingsUpdated = false;
      try {
        const cloudSettingsRec = Array.isArray(cloudData.app_settings)
          ? cloudData.app_settings[0]
          : null;
        if (cloudSettingsRec) {
          let localSettings = {};
          try { localSettings = JSON.parse(localStorage.getItem('maidly_settings')) || {}; } catch { localSettings = {}; }
          const cloudTs = Date.parse(cloudSettingsRec._last_updated_at || 0) || 0;
          const localTs = Date.parse(localSettings._last_updated_at || 0) || 0;
          if (cloudTs > localTs) {
            const { id, ...settingsObj } = cloudSettingsRec; // drop the record id marker
            const prev = this.suppressSync;
            this.suppressSync = true;
            try {
              this.origSetItem('maidly_settings', JSON.stringify(settingsObj));
            } finally {
              this.suppressSync = prev;
            }
            settingsUpdated = true;
          }
        }
      } catch (e) {
        /* settings merge best-effort */
      }
      if (settingsUpdated) {
        // Let the Settings page (and anything showing company info) re-read.
        window.dispatchEvent(new CustomEvent('maidly:settings-updated'));
      }

      return { success: true, types: Object.keys(cloudData) };
    } catch (e) {
      console.error('AutoSync.pullFromCloud failed:', e);
      return { success: false, error: e.message };
    }
  }

  /** Internal: schedule a debounced cloud push for one data_type. */
  schedulePush(dataType, valueStr) {
    if (this.pending.has(dataType)) {
      clearTimeout(this.pending.get(dataType));
    }
    const t = setTimeout(() => {
      this.pending.delete(dataType);
      this.pushNow(dataType, valueStr).catch(() => {});
    }, PUSH_DEBOUNCE_MS);
    this.pending.set(dataType, t);
  }

  /** Internal: schedule a debounced cloud push of the settings object. */
  schedulePushSettings(valueStr) {
    if (this.pending.has('__settings__')) {
      clearTimeout(this.pending.get('__settings__'));
    }
    const t = setTimeout(() => {
      this.pending.delete('__settings__');
      this.pushSettingsNow(valueStr).catch(() => {});
    }, PUSH_DEBOUNCE_MS);
    this.pending.set('__settings__', t);
  }

  /** Internal: stamp + push the settings object to the cloud (last-write-wins). */
  async pushSettingsNow(valueStr) {
    let obj;
    try {
      obj = JSON.parse(valueStr);
    } catch {
      return;
    }
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return;

    const nowIso = new Date().toISOString();
    const stamped = { ...obj, _last_updated_at: nowIso };
    // Persist the stamp locally (suppressed) so future pulls can compare times.
    const prev = this.suppressSync;
    this.suppressSync = true;
    try {
      this.origSetItem('maidly_settings', JSON.stringify(stamped));
    } finally {
      this.suppressSync = prev;
    }
    await syncService.saveSettings(stamped);
  }

  /** Internal: push a single data_type to the cloud + handle deletions. */
  async pushNow(dataType, valueStr) {
    let arr;
    try {
      arr = JSON.parse(valueStr);
    } catch {
      return;
    }
    if (!Array.isArray(arr)) return;

    const prev = this.lastSnapshot.get(dataType) || new Map();
    const currentIds = new Set();
    const nowIso = new Date().toISOString();

    // Diff each item field-by-field against last snapshot, stamping only fields
    // whose value actually changed. New items get every field stamped.
    const stamped = arr.map((item) => {
      if (!item || !item.id) return item;
      currentIds.add(item.id);

      const prevItem = prev.get(item.id);
      const fieldsTs = { ...(item._fields_updated_at || {}) };

      Object.keys(item).forEach((k) => {
        if (isInternalField(k)) return;
        const prevVal = prevItem ? prevItem[k] : undefined;
        if (!prevItem || !deepEqual(prevVal, item[k])) {
          fieldsTs[k] = nowIso;
        }
      });

      return {
        ...item,
        updated_at: item.updated_at || nowIso,
        _last_updated_at: nowIso,
        _last_updated_by_device: syncService.deviceId,
        _fields_updated_at: fieldsTs,
      };
    });

    // Detect deletions: ids that were present last snapshot but missing now.
    const removed = [...prev.keys()].filter((id) => !currentIds.has(id));

    // Update snapshot to the new stamped state.
    const newMap = new Map();
    stamped.forEach((it) => {
      if (it && it.id) newMap.set(it.id, it);
    });
    this.lastSnapshot.set(dataType, newMap);

    // Fire off deletes first (cloud-level) — via durable queue
    await Promise.all(
      removed.map((id) => syncEngine.enqueueDelete(dataType, id))
    );

    // Bulk-save current items — also mirror to IndexedDB for offline read.
    if (stamped.length > 0) {
      try {
        await maidlyDB.records.put(dataType, stamped);
      } catch (e) {
        // IndexedDB write best-effort — cloud copy is still authoritative.
        console.warn('[AutoSync] IndexedDB mirror failed for', dataType, e);
      }
      // Online path: try immediate cloud bulk-save (kept for snappy UX
      // when online). The SyncEngine drain on next tick will pick up any
      // residual unsynced items if this fails.
      try {
        await syncService.bulkSave({ [dataType]: stamped });
      } catch (e) {
        // Fall back: enqueue each record so the queue retries durably
        console.warn('[AutoSync] direct bulkSave failed for', dataType, '— enqueuing for retry', e);
        await Promise.all(
          stamped.map((rec) => syncEngine.enqueueSave(dataType, rec))
        );
      }
    }
  }

  /** Force a full push of every synced key (used after a long offline burst). */
  async pushAll() {
    if (!this.installed || !syncService.getSyncKey() || !navigator.onLine) return;
    // Re-use the per-key push logic so field stamping happens correctly.
    Object.entries(SYNCED_KEYS).forEach(([key]) => {
      const v = window.localStorage.getItem(key);
      if (v) this.pushNow(SYNCED_KEYS[key], v).catch(() => {});
    });
  }

  /** Read items from a given localStorage key as a Map<id, item>. */
  itemMapFromKey(key) {
    const m = new Map();
    try {
      const arr = JSON.parse(window.localStorage.getItem(key) || '[]');
      if (Array.isArray(arr)) {
        arr.forEach((it) => {
          if (it && it.id) m.set(it.id, it);
        });
      }
    } catch (e) {
      // A missing/corrupt key just means no snapshot for this key
      console.warn(`[AutoSync] failed to parse snapshot for ${key}:`, e);
    }
    return m;
  }
}

const autoSync = new AutoSync();
export default autoSync;
